import EventEmitter from 'eventemitter3'
import {
  OPTIONS,
} from './config'
import {
  connect,
  MqttClient,
  IClientPublishOptions,
  IPublishPacket,
  Packet,
  ISubscriptionGrant,
  IClientOptions,
} from 'mqtt/dist/mqtt.min'

export type MqttStatusEvent =
  | { status: 'open' }
  | { status: 'reconnecting' }
  | { status: 'close' }
  | { status: 'error', data: Error }

export type MqttSubscribeCallback = (
  error: Error | null,
  granted: ISubscriptionGrant[]
) => void

export type MqttUnsubscribeCallback = (error?: Error) => void

export class UranusMqtt extends EventEmitter {
  _url: string
  _options?: IClientOptions
  _client: MqttClient | null
  _hasInit: boolean

  constructor (url?: string, options?: IClientOptions) {
    super()
    this._url = url || ''
    this._options = options
    this._client = null
    this._hasInit = false
  }

  initMqtt = () => {
    // 仅初始化一次
    if (this._hasInit) return
    // 建立连接
    this._client = connect(this._url, {
      ...OPTIONS,
      ...this._options,
    })
    this._hasInit = true
    if (this._client) {
      this._client.on('connect', this._onConnect)

      this._client.on('reconnect', this._onReconnect)

      // 消息监听
      this._client.on('message', this._onMessage)

      // 连接关闭
      this._client.on('close', this._onClose)

      // 连接异常
      this._client.on('error', this._onError)
    }
  }

  // 发布
  publishMqtt = (topic: string, body: string | Buffer, opts?: IClientPublishOptions) => {
    if (!this._client?.connected) {
      this.initMqtt()
    }
    this._client?.publish(topic, body, opts || {}, (error?: Error, packet?: Packet) => {
      if (error) {
        window.console.error('mqtt publish error,', error, packet)
      }
    })
  }

  // 订阅
  subscribeMqtt = (topic: string, callback?: MqttSubscribeCallback) => {
    if (!this._client?.connected) {
      this.initMqtt()
    }

    if (!this._client) {
      callback?.(new Error('MQTT client is unavailable'), [])
      return
    }

    this._client.subscribe(topic, (error: Error | null, granted: ISubscriptionGrant[]) => {
      callback?.(error, granted || [])
    })
  }

  // 取消订阅
  unsubscribeMqtt = (topic: string, callback?: MqttUnsubscribeCallback) => {
    this._client?.unsubscribe(topic, callback)
  }

  get connected (): boolean {
    return !!this._client?.connected
  }

  // 更新下一次 CONNECT 使用的凭证（mqtt.js 每次重连都从 options 重建 CONNECT 报文）。
  // 就地更新可保留已注册的订阅与消息回调，避免 JWT 续签时重建客户端。
  updateCredentials = (username: string, password: string) => {
    this._options = { ...(this._options || {}), username, password }
    if (this._client) {
      this._client.options.username = username
      this._client.options.password = password
    }
  }

  // 断开状态下立即用当前凭证重连；已连接时不做任何事。
  reconnect = () => {
    if (!this._client) {
      this.initMqtt()
      return
    }
    if (!this._client.connected) {
      this._client.reconnect()
    }
  }

  // 关闭 mqtt 客户端
  destroyed = () => {
    const client = this._client
    this._client = null
    this._hasInit = false

    if (!client) return

    client.removeListener('connect', this._onConnect)
    client.removeListener('reconnect', this._onReconnect)
    client.removeListener('message', this._onMessage)
    client.removeListener('close', this._onClose)
    client.removeListener('error', this._onError)
    client.end()
  }

  _onConnect = () => {
    this.emit('onStatus', {
      status: 'open',
    } as MqttStatusEvent)
  }

  _onReconnect = () => {
    this.emit('onStatus', {
      status: 'reconnecting',
    } as MqttStatusEvent)
  }

  _onMessage = (topic: string, payload: Buffer, packet: IPublishPacket) => {
    this.emit('onMessageMqtt', { topic, payload, packet })
  }

  _onClose = () => {
    // 连接异常关闭会自动重连
    this.emit('onStatus', {
      status: 'close',
    } as MqttStatusEvent)
  }

  _onError = (error: Error) => {
    // 连接错误会自动重连
    this.emit('onStatus', {
      status: 'error',
      data: error,
    } as MqttStatusEvent)
  }
}

export {
  IClientOptions,
  IPublishPacket,
  IClientPublishOptions,
}
